import { computeTurnover, formatTurnover } from "@/lib/pools/vol-tvl";

import type { Pool } from "../data";
import type { PoolFilters } from "../store/poolsStore";
import type { CoinPair } from "../types";

/**
 * Sanitizes a string to be safe for use in filenames
 * Removes or replaces special characters that are not allowed in filenames
 */
function sanitizeFilename(str: string): string {
	return str
		.replace(/[/\\:*?"<>|]/g, "-") // Replace invalid filename chars with dash
		.replace(/\s+/g, "-") // Replace spaces with dash
		.replace(/-+/g, "-") // Replace multiple dashes with single dash
		.replace(/^-|-$/g, ""); // Remove leading/trailing dashes
}

/**
 * Generates a filename based on active filters and selected coin pair
 * Format: unilytics_[filters]_[timestamp].csv or .json
 * Example: unilytics_BTC-ETH_Ethereum_V3_2024-01-15_14-30-45.csv
 */
export function generateFilename(
	filters: PoolFilters,
	selectedCoinPair: CoinPair | null,
	format: "csv" | "json",
): string {
	const parts: string[] = ["unilytics"];

	// Add coin pair if selected
	if (selectedCoinPair) {
		const [first, second] = selectedCoinPair.pairs;
		parts.push(`${first.symbol}-${second.symbol}`);
	}

	// Add protocol if not "all"
	if (filters.protocol !== "all") {
		parts.push(sanitizeFilename(filters.protocol));
	}

	// Add network if not "all"
	if (filters.network.length > 0) {
		parts.push(sanitizeFilename(filters.network.join("-")));
	}

	// Add fee tier if not "all"
	if (filters.feeTier !== "all") {
		parts.push(sanitizeFilename(filters.feeTier.replace("%", "pct")));
	}

	// Add market cap filter if not "all"
	if (filters.marketCap !== "all") {
		parts.push(sanitizeFilename(filters.marketCap.replace("top_", "top-")));
	}

	// Add keyword if present (truncate to 20 chars)
	if (filters.keyword) {
		const keyword = sanitizeFilename(filters.keyword).substring(0, 20);
		if (keyword) {
			parts.push(keyword);
		}
	}

	// Add timestamp
	const now = new Date();
	const timestamp = now.toISOString().replace(/T/, "_").replace(/:/g, "-").substring(0, 19);
	parts.push(timestamp);

	// Join parts and limit total length to ~200 chars
	let filename = parts.join("_");
	if (filename.length > 200) {
		filename = filename.substring(0, 200);
	}

	return `${filename}.${format}`;
}

/**
 * Converts an array of Pool objects to CSV format
 */
export function exportToCsv(pools: Pool[], includeVolTvl = false): string {
	if (pools.length === 0) {
		return "";
	}

	// Define CSV headers
	const headers = [
		"Pool",
		"Token 1",
		"Token 2",
		"Protocol",
		"Network",
		"Fee Tier",
		"TVL",
		"Volume (1D)",
		"APR (1D)",
		...(includeVolTvl ? ["Vol/TVL (1D)"] : []),
		"APR (7D)",
		...(includeVolTvl ? ["Volume (7D)", "Vol/TVL (7D)"] : []),
		"APR (30D)",
		...(includeVolTvl ? ["Volume (30D)", "Vol/TVL (30D)"] : []),
	];

	// Create CSV rows
	const rows = pools.map((pool) => {
		return [
			pool.pool,
			pool.token1,
			pool.token2,
			pool.uniswap,
			pool.network,
			pool.feeTier,
			pool.tvl,
			pool.volume1d ?? "",
			pool.apr1d,
			...(includeVolTvl ? [formatTurnover(computeTurnover(pool.tvl, pool.volume1d, "1d"))] : []),
			pool.apr7d || "",
			...(includeVolTvl
				? [pool.volume7d ?? "", formatTurnover(computeTurnover(pool.tvl, pool.volume7d, "7d"))]
				: []),
			pool.apr30d || "",
			...(includeVolTvl
				? [pool.volume30d ?? "", formatTurnover(computeTurnover(pool.tvl, pool.volume30d, "30d"))]
				: []),
		].map((field) => {
			// Escape quotes and wrap in quotes if contains comma, quote, or newline
			const str = String(field);
			if (str.includes(",") || str.includes('"') || str.includes("\n")) {
				return `"${str.replace(/"/g, '""')}"`;
			}
			return str;
		});
	});

	// Combine headers and rows
	const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

	return csvContent;
}

/**
 * Converts an array of Pool objects to JSON format
 */
export function exportToJson(pools: Pool[]): string {
	return JSON.stringify(pools, null, 2);
}

/**
 * Triggers a browser download of a file
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
	const blob = new Blob([content], { type: mimeType });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}
